import {
  DetalleDeRevisionSchema,
  EstadoDelAgenteSchema,
  IncidenciasDeCargaSchema,
  OpcionesDeColumnaSchema,
  ResultadoDeAceptarSchema,
  TableroDeCalidadSchema,
  type AceptarCasos,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pedir} from './cliente';

// Agente de calidad (decision 0023): alertas y ultima corrida. Se consulta de
// nuevo cada 5 minutos para que el aviso del menu se mantenga al dia.
export function useAgenteDeCalidad(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ['calidad', 'agente'],
    queryFn: () => pedir('calidad/agente', EstadoDelAgenteSchema),
    refetchInterval: 5 * 60_000,
  });
}

export function useRevisarAhora() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => pedir('calidad/agente/revisar', EstadoDelAgenteSchema, {method: 'POST'}),
    onSuccess: datos => {
      cliente.setQueryData(['calidad', 'agente'], datos);
      void cliente.invalidateQueries({queryKey: ['calidad', 'tablero']});
    },
  });
}

export function useMarcarAlertaVista() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: number | 'todas') =>
      pedir(
        id === 'todas' ? 'calidad/agente/alertas/vistas' : `calidad/agente/alertas/${id}/vista`,
        EstadoDelAgenteSchema,
        {
          method: 'POST',
        },
      ),
    onSuccess: datos => cliente.setQueryData(['calidad', 'agente'], datos),
  });
}

export function useTableroDeCalidad() {
  return useQuery({
    queryKey: ['calidad', 'tablero'],
    queryFn: () => pedir('calidad', TableroDeCalidadSchema),
    staleTime: 60_000,
  });
}

// Orden y filtros por columna de los casos (Daniel, 23/10/2026).
export type VistaDeRevision = {orden?: string; direccion?: 'asc' | 'desc'; porColumna?: string};

const parametrosDeVista = (vista: VistaDeRevision) => {
  const p = new URLSearchParams();
  if (vista.orden) p.set('orden', vista.orden);
  if (vista.orden && vista.direccion === 'desc') p.set('direccion', 'desc');
  if (vista.porColumna) p.set('porColumna', vista.porColumna);
  return p;
};

export function useRevision(clave: string | null, pagina: number, vista: VistaDeRevision = {}, porPagina = 100) {
  const p = parametrosDeVista(vista);
  p.set('pagina', String(pagina));
  p.set('porPagina', String(porPagina));
  const cadena = p.toString();
  return useQuery({
    queryKey: ['calidad', 'revision', clave, cadena],
    queryFn: () => pedir(`calidad/revisiones/${encodeURIComponent(clave!)}?${cadena}`, DetalleDeRevisionSchema),
    enabled: clave !== null,
    placeholderData: keepPreviousData,
  });
}

// Valores de una columna de la revision para su filtro; solo al abrir la lista.
export function useOpcionesDeColumnaDeRevision(
  clave: string,
  porColumna: string | undefined,
  columna: string,
  contiene: string,
  habilitado: boolean,
) {
  const p = parametrosDeVista({porColumna});
  p.set('columna', columna);
  if (contiene) p.set('contiene', contiene);
  const cadena = p.toString();
  return useQuery({
    queryKey: ['calidad', 'opciones-de-columna', clave, cadena],
    queryFn: () =>
      pedir(`calidad/revisiones/${encodeURIComponent(clave)}/opciones-de-columna?${cadena}`, OpcionesDeColumnaSchema),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// Todos los casos de una revision (no solo la pagina a la vista), con su
// enlace, para "Marcar todos" en el cambio masivo. Hasta `maximo` casos, con
// los filtros por columna puestos.
export async function casosDeLaRevision(
  clave: string,
  maximo: number,
  vista: VistaDeRevision = {},
): Promise<Array<{clave: string; enlace: string | null}>> {
  const casos: Array<{clave: string; enlace: string | null}> = [];
  for (let pagina = 1; casos.length < maximo; pagina++) {
    const p = parametrosDeVista(vista);
    p.set('pagina', String(pagina));
    p.set('porPagina', '500');
    const d = await pedir(`calidad/revisiones/${encodeURIComponent(clave)}?${p.toString()}`, DetalleDeRevisionSchema);
    casos.push(...d.filas.map(f => ({clave: f.clave, enlace: f.enlace})));
    if (pagina >= d.totalPaginas) break;
  }
  return casos.slice(0, maximo);
}

// Aceptar casos de una revision (decision 0040): dejan de contar y de salir.
export function useAceptarCasos(revision: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (pedido: AceptarCasos) =>
      pedir(`calidad/revisiones/${encodeURIComponent(revision)}/aceptar`, ResultadoDeAceptarSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(pedido),
      }),
    onSuccess: () => void cliente.invalidateQueries({queryKey: ['calidad']}),
  });
}

export function useIncidenciasDeCarga(id: number | null) {
  return useQuery({
    queryKey: ['calidad', 'incidencias', id],
    queryFn: () => pedir(`calidad/cargas/${id}/incidencias`, IncidenciasDeCargaSchema),
    enabled: id !== null,
  });
}
